import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, RefreshCw, Smartphone, User } from 'lucide-react';

const API = process.env.REACT_APP_API_BASE_URL;

// ─── NOVAA dark tokens (match CIDashboard) ───────────────────────
const T = {
  bg:      'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  border:  'var(--nd-w06)',
  text:    'var(--nd-text)',
  muted:   'var(--nd-tx40)',
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  .cmp-wrap { font-family: 'DM Sans', sans-serif; color: var(--nd-text); }
  .cmp-input {
    background: var(--nd-w05); border: 1px solid var(--nd-w12);
    border-radius: 9px; color: var(--nd-text); font-family: 'DM Sans', sans-serif;
    font-size: 13px; font-weight: 600; padding: 7px 12px; outline: none;
  }
  .cmp-input:focus { border-color: rgba(96,165,250,0.5); }
  .cmp-btn {
    background: var(--nd-w05); border: 1px solid var(--nd-w12);
    border-radius: 9px; color: var(--nd-tx70); font-weight: 700;
    font-size: 12px; padding: 8px 14px; cursor: pointer; display: inline-flex;
    align-items: center; gap: 7px; transition: all 0.15s;
  }
  .cmp-btn:hover { background: var(--nd-w10); color: var(--nd-text-strong); }
  .cmp-table { width: 100%; border-collapse: collapse; }
  .cmp-table th {
    text-align: left; font-size: 9px; font-weight: 700; letter-spacing: 0.15em;
    color: var(--nd-tx35); text-transform: uppercase; padding: 10px 14px;
    border-bottom: 1px solid var(--nd-w08);
  }
  .cmp-table td {
    font-size: 13px; color: var(--nd-text); padding: 12px 14px;
    border-bottom: 1px solid var(--nd-w04);
  }
  .cmp-table tr:hover td { background: var(--nd-w03); }
  .cmp-pill {
    font-size: 10px; font-weight: 700; letter-spacing: 0.06em; padding: 3px 9px;
    border-radius: 20px; font-family: 'DM Mono', monospace; text-transform: uppercase;
    color: #60A5FA; background: rgba(96,165,250,0.12); border: 1px solid rgba(96,165,250,0.25);
  }
`;

const todayStr = () => new Date().toISOString().split('T')[0];
const monthStartStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

// Mobile currently sends the literal string "undefined" for device_info; show a
// dash for that (and other empty-ish values) instead of leaking it to the admin.
const cleanDevice = (d) => {
  if (!d) return '—';
  const s = String(d).trim();
  if (!s || s.toLowerCase() === 'undefined' || s.toLowerCase() === 'null') return '—';
  return s;
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const ComplianceLog = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [from, setFrom] = useState(monthStartStr());
  const [to, setTo] = useState(todayStr());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      const res = await axios.get(`${API}/compliance/acknowledgments?${params.toString()}`);
      setRows(res.data?.data || []);
    } catch (err) {
      console.error('Compliance fetch error:', err);
      setError('Failed to load compliance acknowledgments.');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="cmp-wrap" style={{ background: T.bg, padding: '14px 20px', minHeight: '100vh' }}>
      <style>{STYLES}</style>

      {/* Header */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: '20px 24px', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 42, height: 42, borderRadius: 11, background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ShieldCheck size={22} color="#60A5FA" />
            </div>
            <div>
              <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.18em', marginBottom: 4 }}>NOVAA · C&I SAFETY</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.02em', lineHeight: 1 }}>
                Compliance Acknowledgments
              </div>
              <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>C&I field operatives who accepted the safety protocols</div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 9, color: T.muted, letterSpacing: '0.12em' }}>
              FROM
              <input type="date" className="cmp-input" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 9, color: T.muted, letterSpacing: '0.12em' }}>
              TO
              <input type="date" className="cmp-input" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </label>
            <button className="cmp-btn" onClick={fetchData} title="Refresh">
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.18em', color: 'var(--nd-tx30)' }}>ACKNOWLEDGMENT LOG</div>
          <div style={{ fontSize: 11, color: T.muted, fontFamily: "'DM Mono', monospace" }}>
            {loading ? '…' : `${rows.length} record${rows.length === 1 ? '' : 's'}`}
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: T.muted, fontSize: 13 }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#F87171', fontSize: 13 }}>{error}</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: T.muted, fontSize: 13 }}>
            No acknowledgments in this date range.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="cmp-table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Role</th>
                  <th>Device</th>
                  <th>Acknowledged At</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <User size={14} color="var(--nd-tx35)" />
                        <span style={{ fontWeight: 700 }}>{r.full_name || `User #${r.user_id}`}</span>
                      </div>
                    </td>
                    <td><span className="cmp-pill">{r.role || 'N/A'}</span></td>
                    <td style={{ color: T.muted }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Smartphone size={13} color="var(--nd-tx30)" />
                        {cleanDevice(r.device_info)}
                      </div>
                    </td>
                    <td style={{ fontFamily: "'DM Mono', monospace", color: 'var(--nd-tx70)' }}>
                      {fmtDateTime(r.acknowledged_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceLog;
